import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import Loader from "../../components/Loader.tsx";
import { notification } from "../../utils/Notifications.tsx";
import {
  getCheckClarificationsByBranch,
  updateCheckClarification,
  getCheckClarificationById,
} from "../../services/branch-schedule.service.ts";
import S3Service from "../../services/s3.service.ts";
import ClarificationStats from "./components/ClarificationStats.tsx";
import ClarificationTable from "./components/ClarificationTable.tsx";
import ClarificationDetail from "./components/ClarificationDetail.tsx";
import { readBranches } from "../../services/branch.service.ts";
import Branch from "../../models/Branch.ts";
import CustomSelectField from "../../components/formik/CustomSelectField.tsx";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";

const CheckClarificationsPage = () => {
  const { id: ipParams, clarificationId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  useAppSettings();
  const [loading, setLoading] = useState(false);
  const [clarifications, setClarifications] = useState<any[]>([]);
  const [selectedClarification, setSelectedClarification] = useState<
    any | null
  >(null);
  const [profileImages, setProfileImages] = useState<{ [key: string]: string }>(
    {}
  );
  const [profileUrl, setProfileUrl] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [branchs, setBranchs] = useState<any[]>([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      branch: -99,
    },
    onSubmit: () => {
      fetchClarifications();
    },
  });

  const fetchBranches = async () => {
    try {
      const branches = await readBranches();
      setBranchs(branches);
      
      const defaultBranchId = user?.rol === "Lider de sucursal" 
        ? Number(user.branchId) 
        : Number(ipParams);
      
      const branch = branches.find(
        (branch: Branch) => Number(branch.id) === defaultBranchId
      );
      
      if (branch) {
        await formik.setFieldValue("branch", branch.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const statusOptions = [
    {
      value: "Pendiente",
      label: "Pendiente",
      color: "warning",
      icon: "fa-clock",
    },
    {
      value: "Resuelto",
      label: "Resuelto",
      color: "success",
      icon: "fa-check-circle",
    },
    {
      value: "No resuelto",
      label: "No resuelto",
      color: "danger",
      icon: "fa-times-circle",
    },
    { value: "Cumplio", label: "Cumplió", color: "success", icon: "fa-check" },
    {
      value: "No llego",
      label: "No llegó",
      color: "danger",
      icon: "fa-user-slash",
    },
    {
      value: "Llego tarde",
      label: "Llegó tarde",
      color: "warning",
      icon: "fa-clock",
    },
    {
      value: "Salio tarde",
      label: "Salió tarde",
      color: "warning",
      icon: "fa-clock",
    },
    {
      value: "Descanso trabajado",
      label: "Descanso trabajado",
      color: "info",
      icon: "fa-calendar-check",
    },
    {
      value: "No procede",
      label: "No procede",
      color: "secondary",
      icon: "fa-ban",
    },
    {
      value: "Otro",
      label: "Otro",
      color: "secondary",
      icon: "fa-ellipsis-h",
    },
  ];

  const fetchClarifications = async () => {
    const id = formik.values.branch !== -99 ? Number(formik.values.branch) : null;
    if (!id) return;
    setLoading(true);
    try {
      const response = await getCheckClarificationsByBranch(id, {
        search: searchTerm,
        sortBy,
        order,
        page,
        limit,
      });
      setClarifications(response.results || []);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || 0);

      setProfileImages({});
    } catch (error: any) {
      console.error("Error al cargar aclaraciones:", error);
      notification("Error", "No se pudieron cargar las aclaraciones", "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchClarificationDetail = async () => {
    if (!clarificationId) return;
    setLoading(true);
    try {
      const data = await getCheckClarificationById(Number(clarificationId));
      setSelectedClarification(data);

      if (data?.partner?.person?.curp) {
        try {
          const profileUrlResponse = await S3Service.getFile(
            "PERFIL_" + data.partner.person.curp
          );
          setProfileUrl(profileUrlResponse.url);
        } catch (error) {
          console.error("Error al obtener foto de perfil:", error);
          setProfileUrl("/assets/img/avatar.jpg");
        }
      }
    } catch (error: any) {
      console.error("Error al cargar la aclaración:", error);
      notification("Error", "No se pudo cargar la aclaración", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: {
    status: string;
    adminResponse: string;
    customStatus?: string;
  }) => {
    if (!selectedClarification) return;

    setLoading(true);
    try {
      const finalStatus =
        values.status === "Otro" ? values.customStatus : values.status;

      await updateCheckClarification(selectedClarification.id, {
        status: finalStatus || values.status,
        adminResponse: values.adminResponse || undefined,
      });
      notification("Éxito", "Aclaración actualizada correctamente", "success");

      await fetchClarifications();
      navigate(-1);
    } catch (error: any) {
      notification(
        "Error",
        error?.response?.data?.message || "Error al actualizar la aclaración",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, [user]);

  useEffect(() => {
    if (clarificationId) {
      fetchClarificationDetail();
    } else if (formik.values.branch !== -99) {
      fetchClarifications();
    }
  }, [formik.values.branch, clarificationId, searchTerm, sortBy, order, page, limit]);

  const [allClarifications, setAllClarifications] = useState<any[]>([]);

  useEffect(() => {
    const fetchAllForStats = async () => {
      const id = formik.values.branch !== -99 ? Number(formik.values.branch) : null;
      if (!id) return;
      try {
        const response = await getCheckClarificationsByBranch(id, {
          limit: 10000,
        });
        setAllClarifications(response.results || []);
      } catch (error) {
        console.error(
          "Error al cargar todas las aclaraciones para estadísticas:",
          error
        );
      }
    };
    if (!clarificationId && formik.values.branch !== -99) {
      fetchAllForStats();
    }
  }, [formik.values.branch, clarificationId]);

  const filteredClarifications = allClarifications.filter((clarification) => {
    return clarification.status !== "Sin estado";
  });

  const groupedByStatus = {
    Pendiente: filteredClarifications.filter((c) => c.status === "Pendiente"),
    Resuelto: filteredClarifications.filter((c) => c.status === "Resuelto"),
    "No resuelto": filteredClarifications.filter(
      (c) => c.status === "No resuelto"
    ),
    Cumplio: filteredClarifications.filter((c) => c.status === "Cumplio"),
    "No llego": filteredClarifications.filter((c) => c.status === "No llego"),
    "Llego tarde": filteredClarifications.filter(
      (c) => c.status === "Llego tarde"
    ),
    "Salio tarde": filteredClarifications.filter(
      (c) => c.status === "Salio tarde"
    ),
    "Descanso trabajado": filteredClarifications.filter(
      (c) => c.status === "Descanso trabajado"
    ),
    "No procede": filteredClarifications.filter(
      (c) => c.status === "No procede"
    ),
  };

  if (clarificationId && selectedClarification) {
    return (
      <Layout>
        <Title
          baseTitle="Revisar aclaración"
          basePath={`/sucursales/Aclaraciones-horario/${formik.values.branch !== -99 ? formik.values.branch : ipParams}/revisar/${clarificationId}`}
          title="Revisar y procesar aclaración"
        />

        <Loader isLoading={loading} />

        <ClarificationDetail
          clarification={selectedClarification}
          profileUrl={profileUrl}
          statusOptions={statusOptions}
          onSubmit={handleSubmit}
          loading={loading}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <Title
        baseTitle="Aclaraciones de Horario"
        basePath={`/sucursales/Aclaraciones-horario/${formik.values.branch !== -99 ? formik.values.branch : ipParams}`}
        title="Aclaraciones de Horario"
      />

      <div className="card border-0 m-4 p-4">
        <div className="d-flex flex-row w-100 justify-content-center align-items-end gap-2">
          <div className="w-25">
            <CustomSelectField
              formik={formik}
              field="branch"
              list={branchs}
              label="Sucursal"
              disabled={user?.rol === "Lider de sucursal"}
            />
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />

      <ClarificationStats
        pending={groupedByStatus.Pendiente.length}
        resolved={
          groupedByStatus.Resuelto.length + groupedByStatus.Cumplio.length
        }
        notResolved={
          groupedByStatus["No resuelto"].length +
          groupedByStatus["No llego"].length
        }
        total={filteredClarifications.length}
      />

      <ClarificationTable
        clarifications={clarifications}
        profileImages={profileImages}
        branchId={formik.values.branch !== -99 ? String(formik.values.branch) : ""}
        total={total}
        page={page}
        limit={limit}
        totalPages={totalPages}
        onSearch={(search) => {
          setSearchTerm(search);
          setPage(1);
        }}
        onSort={(sortByField, sortOrder) => {
          setSortBy(sortByField);
          setOrder(sortOrder);
          setPage(1);
        }}
        onPageChange={setPage}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(1);
        }}
        currentSortBy={sortBy}
        currentOrder={order}
        searchTerm={searchTerm}
      />
    </Layout>
  );
};

export default CheckClarificationsPage;
